import {
  catchError,
  finalize,
  from,
  map,
  NEVER,
  Observable,
  of,
  Subject,
  switchMap,
  throwError,
} from "rxjs";
import { ajax, AjaxResponse } from "rxjs/ajax";
import {
  isNullOrUndefined,
  isStrEmpty,
  nullSafetyJSONStringify,
} from "@core/helpers/helpers";
import StorageService from "../storage";
import {
  HttpOptions,
  HttpMethod,
  LoadingMode,
  RequestContentType,
  ProgressOptions,
} from "./http.type";
import { ACCESS_TOKEN_KEY, USER_INFO_KEY } from "@app/constants";
import { Environment } from "@environments/environment";
import { addToast } from "@app/components/toast/toast.service";
import { SystemMessage } from "@app/constants/message.const";
import store from "@app/store";
import { clearUser } from "@app/store/auth/auth.action";
import { redirect } from "react-router-dom";

/**
 * GET nhưng là thao tác của người dùng (bật/tắt/xoá máy, kết nối lại, tunnel, play, OTP…):
 * vẫn che cả trang như trước để tránh bấm 2 lần. Các GET khác chỉ là tải dữ liệu.
 */
const ACTION_GET = [
  /^\/image-cloud\/(start|leave-queue|restart-vm|delete-vm|backup-vm|restore-vm|shutdown-vm|extend-vm)\//,
  /^\/images\/(wol|reconnect)\//,
  /^\/play-pcrender\//,
  /^\/sshs\/choose-tunnel/,
  /^\/auth\/otp/,
  /^\/accounts\/verify-phone/,
];

class _HttpService {
  /** true khi đang có thao tác cần che cả trang (lớp Loading) */
  public isRequesting$ = new Subject<boolean>();
  /** true khi đang tải dữ liệu (thanh mảnh trên đầu trang, không che trang) */
  public isLoading$ = new Subject<boolean>();

  // đếm số request đang chạy để nhiều request song song không làm loading tắt sớm
  private overlayCount = 0;
  private barCount = 0;
  // > 0 khi đang tạo request bên trong local(): nút bấm tự hiện trạng thái đang chạy (useBusy), không che cả trang
  private localDepth = 0;

  /**
   * Tạo request mà không hiện lớp Loading che cả trang: nơi gọi tự xoay chính nút vừa bấm (xem useBusy).
   * Toast lỗi vẫn hiện như cũ. Ở môi trường dev lỗi vẫn trả về cho nơi gọi (để nút thôi xoay), giống bản build.
   */
  public local<T>(make: () => T): T {
    this.localDepth++;

    try {
      return make();
    } finally {
      this.localDepth--;
    }
  }
  public onError$ = new Subject<any>();

  private _commonHeader = {
    "Content-Type": "application/json",
  };

  public get<T>(uri: string, options?: HttpOptions): Observable<T> {
    return this.request(uri, HttpMethod.GET, options);
  }

  public post<T>(uri: string, options?: HttpOptions): Observable<T> {
    return this.request(uri, HttpMethod.POST, options);
  }

  public put<T>(uri: string, options?: HttpOptions): Observable<T> {
    return this.request(uri, HttpMethod.PUT, options);
  }

  public patch<T>(uri: string, options?: HttpOptions): Observable<T> {
    return this.request(uri, HttpMethod.PATCH, options);
  }

  public delete<T>(uri: string, options?: HttpOptions): Observable<T> {
    return this.request(uri, HttpMethod.DELETE, options);
  }

  public requestUpload<T>(
    uri: string,
    method = HttpMethod.POST,
    progressHandler: (ajaxResponse: AjaxResponse<any>) => void,
    options?: HttpOptions
  ): Observable<T> {
    const headers = {
      ...options?.headers,
      "Content-Type": "application/octet-stream",
    };

    const newOptions: HttpOptions = {
      ...options,
      headers,
      requestContentType: RequestContentType.BINARY_STREAM,
    };

    return this.request(uri, method, newOptions, {
      includeUploadProgress: true,
      progressHandler,
    });
  }

  public requestDownload(uri: string, options?: HttpOptions) {
    const token = this.getAccessToken();
    const url = this.resolveUri(uri);

    return ajax({
      url,
      method: HttpMethod.GET,
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        ...options?.headers,
      },
      responseType: "blob" as "json",
    }).pipe(
      // Lỗi khi tải file (PCRender Tunnel, RDP, hợp đồng…) trả về dạng Blob nên trước đây không ai báo:
      // bấm nút xong không thấy gì. Đọc message trong body rồi báo bằng toast như các request khác.
      catchError((error) => {
        const blob: unknown = error?.response;
        const text$ = blob instanceof Blob ? from(blob.text()) : of("");

        return text$.pipe(
          switchMap((text) => {
            let message = error?.message || SystemMessage.UNKNOWN_ERROR;

            try {
              message = JSON.parse(text)?.message || message;
            } catch {
              // body không phải JSON: giữ message mặc định
            }

            if (!options?.silent) {
              addToast({ text: message, status: "inValid" });
            }

            if (error?.status === 401) {
              StorageService.remove(ACCESS_TOKEN_KEY);
              StorageService.remove(USER_INFO_KEY);
              store.dispatch(clearUser());
              redirect("/login");
            }

            return throwError(() => error);
          })
        );
      })
    );
  }

  private request<T>(
    uri: string,
    method: string,
    options?: HttpOptions,
    progressOptions?: ProgressOptions
  ): Observable<T> {
    const token = this.getAccessToken();
    let url = this.resolveUri(uri);

    if (options?.queryParams) {
      url = url + "?" + this.generateHttpParams(options?.queryParams);
    }

    let body: any = nullSafetyJSONStringify(this.buildBodyData(options?.body));

    if (options?.requestContentType) {
      switch (options?.requestContentType) {
        case RequestContentType.MULTIPART:
          body = this.buildFormData(options?.body);
          break;
        case RequestContentType.BINARY_STREAM:
          body = options.body;
          break;
      }
    }

    // Tải dữ liệu (chuyển tab, lọc, phân trang) không che cả trang nữa, tránh trang bị nháy
    const local = this.localDepth > 0;
    const loading = local ? "none" : this.loadingModeOf(uri, method, options);
    let ended = false;
    const endLoading = () => {
      if (ended) return;

      ended = true;
      this.endLoading(loading);
    };

    this.beginLoading(loading);

    return ajax({
      url,
      method,
      body,
      headers: {
        ...(options?.requestContentType === RequestContentType.MULTIPART
          ? { Accept: "application/json" }
          : this._commonHeader),
        Authorization: token ? `Bearer ${token}` : "",
        ...options?.headers,
      },
      includeUploadProgress: progressOptions?.includeUploadProgress,
    }).pipe(
      map((ajaxResponse) => {
        progressOptions?.progressHandler &&
          progressOptions?.progressHandler(ajaxResponse);

        return this.handleResponse<T>(ajaxResponse);
      }),
      // catchError((error) => {
      //   this.onError$.next(error);

      //   const message = error?.response?.message ?? SystemMessage.UNKNOWN_ERROR;

      //   addToast({ text: message, status: "inValid" });

      //   if (error?.response?.statusCode === 401) {
      //     StorageService.remove(ACCESS_TOKEN_KEY);
      //     StorageService.remove(USER_INFO_KEY);
      //     store.dispatch(clearUser());
      //     redirect("/login");
      //   }

      //   if (process.env.NODE_ENV === "development") {
      //     this.isRequesting$.next(false);

      //     return NEVER;
      //   }

      //   return throwError(() => error);
      // }),
      //fix 429
      catchError((error) => {
        this.onError$.next(error);

        // 1. Kiểm tra status code đúng chuẩn RxJS AjaxError
        const statusCode = error?.status || error?.response?.statusCode;

        // 2. Lấy thông điệp lỗi: Ưu tiên error.response.error (cho case 429),
        // sau đó đến error.response.message hoặc error.message mặc định
        const message =
          error?.response?.error ||
          error?.response?.message ||
          error?.message ||
          SystemMessage.UNKNOWN_ERROR;

        if (!options?.silent) {
          addToast({ text: message, status: "inValid" });
        }

        if (statusCode === 401) {
          StorageService.remove(ACCESS_TOKEN_KEY);
          StorageService.remove(USER_INFO_KEY);
          store.dispatch(clearUser());
          redirect("/login");
        }

        if (process.env.NODE_ENV === "development" && !options?.silent && !local) {
          endLoading();

          return NEVER;
        }

        return throwError(() => error);
      }),
      finalize(endLoading)
    );
  }

  private loadingModeOf(uri: string, method: string, options?: HttpOptions): LoadingMode {
    if (options?.silent) return "none";

    if (options?.loading) return options.loading;

    if (method !== HttpMethod.GET) return "overlay";

    return ACTION_GET.some((pattern) => pattern.test(uri)) ? "overlay" : "bar";
  }

  private beginLoading(mode: LoadingMode) {
    if (mode === "overlay" && ++this.overlayCount === 1) this.isRequesting$.next(true);

    if (mode === "bar" && ++this.barCount === 1) this.isLoading$.next(true);
  }

  private endLoading(mode: LoadingMode) {
    if (mode === "overlay" && this.overlayCount > 0 && --this.overlayCount === 0) this.isRequesting$.next(false);

    if (mode === "bar" && this.barCount > 0 && --this.barCount === 0) this.isLoading$.next(false);
  }

  protected buildBodyData(data: any) {
    return data || Object.create(null);
  }

  protected buildFormData(data: any) {
    const formData = new FormData();

    for (const key in data) {
      if (data[key]) {
        if (data[key] instanceof File) {
          formData.append(key, data[key], data[key].name);
        } else if (Array.isArray(data[key])) {
          for (const item of data[key]) {
            formData.append(key, item);
          }
        } else {
          formData.append(key, data[key]);
        }
      }
    }

    return formData;
  }

  public handleResponse<T>(ajaxResponse: AjaxResponse<any>): T {
    return ajaxResponse.response;
  }

  private resolveUri(uri: string): string {
    if (/^(http|https):\/\/.+$/.test(uri)) {
      return uri;
    }

    return `${Environment.BASE_API}${uri}`;
  }

  private generateHttpParams(params: object) {
    const httpParams: string[] = [];
    const objectToQueryString = (obj: object, prefix?: any) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const k = prefix ? prefix + "[" + key + "]" : key;
          const v = (obj as any)[key];

          if (Array.isArray(v)) {
            for (const vv of v) {
              httpParams.push(k + "=" + vv);
            }
          } else if (v !== null && typeof v === "object") {
            objectToQueryString(v, k);
          } else {
            if (!isNullOrUndefined(v) && !isStrEmpty(v.toString())) {
              httpParams.push(k + "=" + v);
            }
          }
        }
      }
    };

    objectToQueryString(params);

    return encodeURI(httpParams.join("&"));
  }

  public getAccessToken() {
    return (
      StorageService.get(ACCESS_TOKEN_KEY) ||
      StorageService.getSession(ACCESS_TOKEN_KEY)
    );
  }
}

const HttpService = new _HttpService();

export default HttpService;
